import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { CalendarDays, ChevronDown } from "lucide-react";
import type { DateRange as DayPickerRange } from "react-day-picker";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type DateRange = { from: string; to: string }; // YYYY-MM-DD (fecha "local")

type Preset = { key: string; label: string; range: () => DateRange };

// ----- helpers de fecha (evitan pasar por ISO/UTC) -----

function todayLocal(): Date {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function fromISO(s: string): Date {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

// ----- presets -----

const PRESETS: Preset[] = [
  {
    key: "today",
    label: "Hoy",
    range: () => {
      const t = todayLocal();
      return { from: toISO(t), to: toISO(t) };
    },
  },
  {
    key: "last7",
    label: "Últimos 7 días",
    range: () => {
      const t = todayLocal();
      const from = new Date(t);
      from.setDate(from.getDate() - 6);
      return { from: toISO(from), to: toISO(t) };
    },
  },
  {
    key: "this_month",
    label: "Este mes",
    range: () => {
      const t = todayLocal();
      return { from: toISO(new Date(t.getFullYear(), t.getMonth(), 1)), to: toISO(t) };
    },
  },
  {
    key: "last30",
    label: "Últimos 30 días",
    range: () => {
      const t = todayLocal();
      const from = new Date(t);
      from.setDate(from.getDate() - 29);
      return { from: toISO(from), to: toISO(t) };
    },
  },
  {
    key: "last_month",
    label: "Mes pasado",
    range: () => {
      const t = todayLocal();
      const from = new Date(t.getFullYear(), t.getMonth() - 1, 1);
      const to = new Date(t.getFullYear(), t.getMonth(), 0);
      return { from: toISO(from), to: toISO(to) };
    },
  },
  {
    key: "last90",
    label: "Últimos 3 meses",
    range: () => {
      const t = todayLocal();
      const from = new Date(t);
      from.setDate(from.getDate() - 89);
      return { from: toISO(from), to: toISO(t) };
    },
  },
  {
    key: "this_year",
    label: "Este año",
    range: () => {
      const t = todayLocal();
      return { from: toISO(new Date(t.getFullYear(), 0, 1)), to: toISO(t) };
    },
  },
];

function matchPreset(v: DateRange): string | null {
  for (const p of PRESETS) {
    const r = p.range();
    if (r.from === v.from && r.to === v.to) return p.key;
  }
  return null;
}

function formatRangeLabel(v: DateRange): string {
  const from = fromISO(v.from);
  const to = fromISO(v.to);
  if (v.from === v.to) return format(from, "d MMM yyyy", { locale: es });
  if (from.getFullYear() === to.getFullYear()) {
    return `${format(from, "d MMM", { locale: es })} – ${format(to, "d MMM yyyy", { locale: es })}`;
  }
  return `${format(from, "d MMM yyyy", { locale: es })} – ${format(to, "d MMM yyyy", { locale: es })}`;
}

// ----- componente -----

type Props = {
  value: DateRange;
  onChange: (r: DateRange) => void;
  align?: "start" | "end";
};

export function DateRangePicker({ value, onChange, align = "end" }: Props) {
  const [open, setOpen] = useState(false);

  // `draft` = lo que el usuario está construyendo dentro del popover.
  const [draft, setDraft] = useState<DayPickerRange | undefined>(undefined);

  const activePreset = useMemo(() => matchPreset(value), [value]);
  const presetLabel = activePreset ? PRESETS.find((p) => p.key === activePreset)?.label : "Personalizado";

  // Al abrir el popover: cargar el value actual como draft (para ver el rango
  // vigente resaltado en el calendario). Sólo depende de `open`.
  useEffect(() => {
    if (!open) return;
    setDraft({ from: fromISO(value.from), to: fromISO(value.to) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const draftComplete = !!(draft?.from && draft?.to);

  // Regla de selección:
  // - Si draft ya tenía from Y to (rango completo, ya sea del value inicial o
  //   de una selección previa) → un nuevo clic **reinicia** el rango desde ese día.
  // - Si draft sólo tenía `from` → completar el rango.
  // - Si no había nada → primer clic setea el `from`.
  function handleSelect(
    range: DayPickerRange | undefined,
    selectedDay: Date | undefined
  ) {
    if (!selectedDay) {
      setDraft(undefined);
      return;
    }
    if (draft?.from && draft?.to) {
      setDraft({ from: selectedDay, to: undefined });
      return;
    }
    setDraft(range);
  }

  function applyPreset(p: Preset) {
    onChange(p.range());
    setOpen(false);
  }

  function applyDraft() {
    if (draft?.from && draft?.to) {
      onChange({ from: toISO(draft.from), to: toISO(draft.to) });
      setOpen(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-2">
          <CalendarDays className="h-4 w-4" />
          <span className="font-normal text-muted-foreground">{presetLabel}</span>
          <span>·</span>
          <span>{formatRangeLabel(value)}</span>
          <ChevronDown className="h-4 w-4 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-auto p-0">
        <div className="flex">
          {/* Columna de presets: accesos rápidos */}
          <div className="flex flex-col border-r p-2 min-w-[170px]">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                className={cn(
                  "text-left px-3 py-1.5 rounded text-sm hover:bg-accent",
                  activePreset === p.key && "bg-accent font-medium"
                )}
                onClick={() => applyPreset(p)}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Calendario siempre visible */}
          <div className="p-2 flex flex-col gap-2">
            <Calendar
              mode="range"
              numberOfMonths={2}
              selected={draft}
              onSelect={handleSelect}
              defaultMonth={draft?.from ?? fromISO(value.from)}
            />
            <div className="flex items-center justify-between gap-2 border-t pt-2 text-sm">
              <span className="text-muted-foreground min-h-[1.5rem]">
                {!draft?.from && "Selecciona la fecha inicial"}
                {draft?.from && !draft?.to &&
                  `Desde ${format(draft.from, "d MMM yyyy", { locale: es })} — elige fecha final`}
                {draft?.from && draft?.to &&
                  `${format(draft.from, "d MMM", { locale: es })} – ${format(draft.to, "d MMM yyyy", { locale: es })}`}
              </span>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setDraft(undefined)}>
                  Limpiar
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
                  Cancelar
                </Button>
                <Button size="sm" disabled={!draftComplete} onClick={applyDraft}>
                  Aplicar
                </Button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
